import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import LoadingSpinner from '../../partials/LoadingSpinner';

import ThresholdLine from './ThresholdLine';

import { Button, Text, H2, H4, Panel, Dropdown, Small, Search, Collapse, Box, Switch, Pagination, Input } from '@bigcommerce/big-design';
import { ArrowDropDownIcon } from '@bigcommerce/big-design-icons';

import { updateSelectedItems, searchProducts, resetProducts } from '../../../redux/actions/products';
import { getCategories } from '../../../redux/actions/categories';
import { createThresholds, reset } from '../../../redux/actions/thresholds';

const ThresholdSearch = ({ payload, activeAddress, addThresholds, isSearching, searchedProducts, searchedProductsPagination, searchedVariants, selectedProductList, categories, submitSearch, loadCategories, setSelectedProductList, resetProductSearch, thresholds, resetThresholds }) => {
  const [submitted, setSubmitted] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [inStockFilter, setInStockFilter] = useState(false);
  const [purchasingDisabledFilter, setPurchasingDisabledFilter] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState();

  const [thresholdValues, setThresholdValues] = useState({
    'targetInventory': { 'product': {}, 'variant': {} },
    'lowInventory': { 'product': {}, 'variant': {} },
    'criticalInventory': { 'product': {}, 'variant': {} }
  })

  const [enqueueSearch, setEnqueueSearch] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    resetProductSearch();
    resetThresholds();
    loadCategories(payload);
    setThresholdValues({
      'targetInventory': { 'product': {}, 'variant': {} },
      'lowInventory': { 'product': {}, 'variant': {} },
      'criticalInventory': { 'product': {}, 'variant': {} }
    });
    setSelectedProductList({ variants: {}, products: {} });
  }, []);

  useEffect(() => {
    if(searchTerm){
      setCurrentPage(1);
      handleSearch(searchTerm, 1);
    }
  }, [activeAddress]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(searchTerm !== ''){
        setCurrentPage(1);
        handleSearch(searchTerm, 1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, 500]);

  const handleSearch = (searchTerm, page) => {
    if(!isSearching) {
      setEnqueueSearch(null);
      submitSearch(payload, searchTerm, inStockFilter, purchasingDisabledFilter, categoryFilter, page);
    } else {
      setEnqueueSearch(searchTerm);
    }
  }
  
  useEffect(() => {
    if(!isSearching) {
      readySearch();
    }
  }, [isSearching]);

  const readySearch = () => {
    if(enqueueSearch) {
      submitSearch(payload, enqueueSearch, inStockFilter, purchasingDisabledFilter, categoryFilter, currentPage);
      setEnqueueSearch(null);
    }
  }
  
  const toggleInStockFilter = () => {
    setInStockFilter(!inStockFilter);
  }

  const togglePurchasingDisabledFilter = () => {
    setPurchasingDisabledFilter(!purchasingDisabledFilter);
  }
  
  const categoryItems = () => {
    let items = [];

    Object.entries(categories.categories).map(([key, name]) =>
      items.push(
        {
          content: name,
          onItemClick: (event) => switchCategoryFilter(event, setCategoryFilter),
          hash: key
        }
      )
    )

    return items;
  }

  const switchCategoryFilter = (event, setter) => {
    let categoryId = event.hash;
    setter(categoryId);
  }

  const setProductResultPage = (page) => {
    setCurrentPage(page);
    handleSearch(searchTerm, page);
  }

  const clearCategory = () => {
    setCategoryFilter(null);
  }

  const validateTerm = (event) => {
    let value = event.target.value;

    setSearchTerm(value);
  }

  const setTargetInventory = (itemId, type, event) => {
    let assignValues = {...thresholdValues};
    assignValues.targetInventory[type][itemId] = event.target.value;
    setThresholdValues(assignValues);
  }

  const setLowInventory = (itemId, type, event) => {
    let assignValues = {...thresholdValues};
    assignValues.lowInventory[type][itemId] = event.target.value;
    setThresholdValues(assignValues);
  }

  const setCriticalInventory = (itemId, type, event) => {
    let assignValues = {...thresholdValues};
    assignValues.criticalInventory[type][itemId] = event.target.value;
    setThresholdValues(assignValues);
  }

  const validatePage = (event) => {
    let page = event.target.value;

    if (page < 1) {
      page = 1;
    }

    if (page > searchedProductsPagination.total_pages) {
      page = searchedProductsPagination.total_pages;
    }

    setCurrentPage(page);
  }

  const submitPage = () => {
    handleSearch(searchTerm, currentPage);
  }

  const handleSubmit = () => {
    setSubmitted(true);
    addThresholds(payload, activeAddress.id, selectedProductList, thresholdValues);
  }

  return(
    <>
      <Panel>
        <div className="full-width">
          <Box marginBottom="medium">
            <H4>Catalog Search</H4>
            <Search onChange={validateTerm} onSubmit={() => handleSearch(searchTerm)} value={searchTerm} />
            <Collapse title="Filters">
              <div className="background-darken padding-20 flex-container">
                <div className="flex-1">
                  <H4>In Stock</H4>
                  <Switch checked={inStockFilter} onChange={toggleInStockFilter} />
                </div>
                <div className="flex-1">
                  <H4>Purchasing Disabled</H4>
                  <Switch checked={purchasingDisabledFilter} onChange={togglePurchasingDisabledFilter} />
                </div>
                <div className="flex-2">
                  <H4>Cagetory</H4>
                  { !categories.isFetching && categories.status === "success" &&
                    <>
                      <Dropdown
                        items={categoryItems()}
                        maxHeight={250}
                        placement="bottom-start"
                        toggle={<Button iconRight={<ArrowDropDownIcon />}>{categories.categories[categoryFilter] || "Select Category"}</Button>}
                      />
                      <Button variant="subtle" className="left-15 bottom-5" onClick={() => clearCategory()}>
                        Clear Category
                      </Button>
                    </>
                  }
                  { categories.isFetching &&
                    <LoadingSpinner size="xxSmall" statusText="Loading categories"/>
                  }
                </div>
              </div>
              <hr/>
            </Collapse>
          </Box>
        </div>
      </Panel>

      <Panel>
        { !isSearching && searchedProducts === null && searchTerm === '' &&
          <Box
            backgroundColor="primary10"
            border="box"
            borderRadius="normal"
            padding="medium"
          >
            <H4>Search Results</H4>
            <Text>
              Search your store's catalog above to see live results here
            </Text>
          </Box>
        }

        { isSearching && <LoadingSpinner size="small" statusText="Searching products" /> }

        { !isSearching && searchedProducts !== null && 
        <>
          <Button
            onClick={() => handleSubmit()}
            disabled={ submitted || ((Object.keys(selectedProductList.products).length + Object.keys(selectedProductList.variants).length) === 0) }
            isLoading={ submitted }
          >
            Submit New Product Inventory Thresholds
          </Button>
          
          <div>
            { searchedProductsPagination.total_pages > 1 &&
              <div className="flex-container">
                <div className="flex-1">
                  <Pagination
                    currentPage={searchedProductsPagination.current_page}
                    itemsPerPage={50}
                    itemsPerPageOptions={[]}
                    onItemsPerPageChange={null}
                    onPageChange={(newPage) => setProductResultPage(newPage)}
                    totalItems={searchedProductsPagination.total}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex-container">
                    <div className="flex-1">
                      <div className="right-15">
                        <Input
                          required
                          onChange={validatePage}
                          placeholder="Page"
                          value={currentPage}
                          type="number"
                          disabled={submitted}
                        />
                      </div>
                    </div>

                    <div className="flex-8">
                      <Text as="span">
                        of {searchedProductsPagination.total_pages} pages &nbsp;
                      </Text>

                      <Button className="left-15" onClick={() => submitPage()} disabled={submitted}>Set Page</Button>
                    </div>
                  </div>
                </div>
              </div>
            }
            { searchedProducts?.map(product => 
              <>
                <ThresholdLine
                  payload={payload}
                  type={'product'}
                  productId={product.id}
                  itemId={product.id}
                  options={false}
                  name={product.name}
                  imageUrl={product.primary_image?.url_tiny}
                  fullImageUrl={product.primary_image?.url_standard}
                  rowId={product.id}
                  price={product.price}
                  quantity={product.inventory_level}
                  inventoryTracking={product.inventory_tracking}
                  hasVariants={searchedVariants[product.id]?.length > 0}
                  binPickingNumber={product.bin_picking_number}
                  targetInventory={thresholdValues.targetInventory.product[product.id]}
                  setTargetInventory={setTargetInventory}
                  lowInventory={thresholdValues.lowInventory.product[product.id]}
                  setLowInventory={setLowInventory}
                  setCriticalInventory={setCriticalInventory}
                  criticalInventory={thresholdValues.criticalInventory.product[product.id]}
                  activeAddressId={activeAddress.id}
                  submitted={submitted}
                />
                { searchedVariants[product.id]?.map(variant =>
                  <ThresholdLine
                    payload={payload}
                    type={'variant'}
                    productId={product.id}
                    itemId={variant.id}
                    name={product.name}
                    imageUrl={product.primary_image?.url_tiny}
                    fullImageUrl={product.primary_image?.url_standard}
                    options={variant.option_values}
                    price={variant.price || product.price}
                    quantity={variant.inventory_level}
                    inventoryTracking={false}
                    hasVariants={false}
                    binPickingNumber={variant.bin_picking_number}
                    targetInventory={thresholdValues.targetInventory.variant[variant.id]}
                    setTargetInventory={setTargetInventory}
                    lowInventory={thresholdValues.lowInventory.variant[variant.id]}
                    setLowInventory={setLowInventory}
                    setCriticalInventory={setCriticalInventory}
                    criticalInventory={thresholdValues.criticalInventory.variant[variant.id]}
                    activeAddressId={activeAddress.id}
                    submitted={submitted}
                  />
                )}
              </>
            )}
          </div>
        </>
      }
      </Panel>
    </>
  )
}

const mapStateToProps = state => ({
  isSearching: state.products.isFetching,
  searchedProducts: state.products.searchedProducts,
  searchedProductsPagination: state.products.searchedProductsPagination,
  searchedVariants: state.products.searchedVariants,
  selectedProductList: state.products.selectedProductList,
  categories: state.categories,
  thresholds: state.thresholds,
});

const mapDispatchToProps = (dispatch) => ({
  submitSearch: (payload, searchTerm, inStockFilter, purchasingDisabledFilter, categoryFilter, page) => dispatch(searchProducts(payload, searchTerm, inStockFilter, purchasingDisabledFilter, categoryFilter, page)),
  loadCategories: (payload) => dispatch(getCategories(payload)),
  setSelectedProductList: (items) => dispatch(updateSelectedItems(items)),

  addThresholds: (payload, locationId, thresholds, thresholdValues) => dispatch(createThresholds(payload, locationId, thresholds, thresholdValues)),
  resetThresholds: () => dispatch(reset()),
  resetProductSearch: () => dispatch(resetProducts()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThresholdSearch);
